import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
// @mui
import { Box, Drawer, IconButton, Tooltip } from '@mui/material';
import SettingsMenu from 'src/components/SettingsMenu';
import SuperAdminMenu from 'src/components/SuperAdminMenu';
// hooks
import { PATH_DASHBOARD } from 'src/routes/paths';
import AddUserPopup from 'src/components/dialogPopUp/AddUserPopup';
import { userIconWrapperStyle, userIconInnerStyle } from 'src/styles/NavBarVerticalStyle';
import { shallowEqual, useSelector } from 'react-redux';
import { widgetLoadingStyle } from 'src/styles/widgetCounterStyle';
import { dispatch } from 'src/redux/store';
import { fetchMyPermissions } from 'src/redux/slices/permissions';
import useFeaturePermission from 'src/hooks/useFeaturePermission';
import { useAuth, useResponsive } from '../../../hooks';
// config
import { NAV } from '../../../config';
// components
import { Iconify, Label, Scrollbar } from '../../../components';
import { SvgColor } from '../../../components/svg-color';
import { NavSectionVertical } from '../../../components/nav-section';
//
import getNavConfig from './NavConfig';
import NavbarDocs from './NavbarDocs';
import CollapseButton from './CollapseButton';
import NavbarSkeleton from './NavbarSkeleton';

// Module-level frozen sentinel so an unset `hidden_parent_tables` preference
// resolves to the SAME reference on every selector call (see #80 fix below).
const EMPTY_HIDDEN_PARENT_TABLES = Object.freeze([]);

// ----------------------------------------------------------------------

export default function NavbarVertical({ openNav, onCloseNav }) {
    const [userOpen, setUserOpen] = useState(false);
    const location = useLocation()
    const { settingsMenuActive, selectedPracticeForMenu } = useSelector((state) => state.mergeTags);
    const {
        whatsappNotifCount,
    } = useSelector(
        (state) => ({
            whatsappNotifCount: state?.whatsappV2?.whatsappNotifCount,
        }),
        shallowEqual
    );

    const isDesktop = useResponsive('up', 'lg');

    const [whatsappConfig, setWhatsappConfig] = React.useState([]);
    const [consentVersion] = React.useState('v1');

    const { practiceUser: { results } } = useSelector(state => state.practiceUser);
    const practiceId = useSelector(state => state.practiceTreatmentList?.practiceId);
    const hasMultiplePractices = useSelector(state => (state.practiceTreatment?.results?.length || 0) > 1);
    const hasGroupMembership = useSelector(state => (state.groupDashboard?.groups?.length || 0) > 0);
    const hasTasksV2 = useSelector(state => {
        const perm = state.permissions?.permissions?.['tasks-v2'];
        return typeof perm === 'string' && perm !== 'hidden';
    });
    // Select the raw value (may be undefined). Returning `|| []` from the selector
     // produces a NEW array reference on every Redux state emit, which (a) makes the
     // useSelector return a "changed" value every render and (b) re-fires the effect
     // below that has `hiddenParentTables` in deps, dispatching setWhatsappConfig in
     // a loop → "Maximum update depth exceeded" → React halts → ConsultantAttend's
     // Suspense fallback stays mounted → `/leads/?` never fires (#80). Memoise the
     // fallback so the reference is stable across renders.
    const hiddenParentTablesRaw = useSelector(state => state.practice?.practice?.preferences?.hidden_parent_tables);
    const hiddenParentTables = React.useMemo(() => hiddenParentTablesRaw || EMPTY_HIDDEN_PARENT_TABLES, [hiddenParentTablesRaw]);
    const { user } = useAuth();
    const practitionerDashboard = useFeaturePermission('practitioner-dashboard');
    const isPractitionerFlagUser = practitionerDashboard?.canView && !practitionerDashboard?.canManage;

    const getUserList = () => results.filter(el => el.user && el.user.id === user?.pk);
    const isPractitioner = getUserList()[0]?.role === "Practitioner"

    React.useEffect(() => {
        if (practiceId) {
            dispatch(fetchMyPermissions(practiceId));
        }
    }, [practiceId]);

    React.useEffect(() => {
        const whatsappCount = whatsappNotifCount ?? 0;
        const newNavConfig = [];
        const navConfig = getNavConfig(practiceId, { isSuperAdmin: getUserList()[0]?.user?.is_superuser, userRole: getUserList()[0]?.role, hasMultiplePractices, consentVersion, hasGroupMembership, hasTasksV2, hiddenParentTables });
        if (results?.length) {
            if (isPractitioner && isPractitionerFlagUser) {
                newNavConfig.push({ subheader: 'dashboard', items: [{ title: 'Dashboard', path: PATH_DASHBOARD.general.app, icon: <SvgColor src={`/assets/icons/navbar/ic_dashboard.svg`} /> }] });
            } else if ((!isPractitioner && !isPractitionerFlagUser) || (isPractitioner && isPractitionerFlagUser !== null)) {
                navConfig?.forEach((config) => {
                    if (config?.subheader === 'dashboard') {
                        const items = [];
                        config?.items?.forEach((item) => {
                            if (item?.title === 'Whatsapp') {
                                items.push({
                                    ...item, info: whatsappCount > 0 ? (
                                        <Label sx={{ color: 'rgba(0, 171, 85, 1)' }} startIcon={<Iconify icon="solar:bell-bing-bold-duotone" />}>
                                            {`${whatsappCount} NEW`}
                                        </Label>
                                    ) : ''
                                });
                            } else {
                                items.push(item);
                            }
                        });
                        newNavConfig.push({ subheader: 'dashboard', items });
                    } else {
                        newNavConfig.push(config);
                    }
                });
            }
        }
        setWhatsappConfig(newNavConfig);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [whatsappNotifCount, isPractitionerFlagUser, results, practiceId, hasMultiplePractices, consentVersion, hasGroupMembership, hasTasksV2, hiddenParentTables]);

    useEffect(() => {
        if (openNav) {
            onCloseNav();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);


    // Navbar mode is driven by URL. Settings pages live under
    // /dashboard/practice/* (renamed from /dashboard/user/* in 127c20c4) and
    // render <SettingsMenu />. /dashboard/practice/:practiceId/<feature> URLs
    // (lead-detail, archived, tasks-v2, whatsapp, instagram) share the prefix
    // but are feature pages — they fall through to the main nav.
    const isSuperAdminPath = location.pathname.includes('/super-admin');
    const isPracticeFeaturePath = /\/dashboard\/practice\/\d+\/(lead-detail|archived|tasks-v2|whatsapp|instagram)/.test(location.pathname);
    const isSettingsPath = settingsMenuActive
        && location.pathname.includes('/dashboard/practice/')
        && !isPracticeFeaturePath
        && !location.pathname.includes('/practice/profile')
        && !location.pathname.includes('/practice/notification-table')
        && !location.pathname.includes('/practice/facebook')
        && !location.pathname.includes('/practice/create-chatbot')
        && !location.pathname.includes('/practice/edit-chatbot')
        && !location.pathname.includes('/practice/consent')
        && !location.pathname.includes('/practice/create-consent')
        && !location.pathname.includes('/edit-consent');

    const renderContent = (
        <Scrollbar
            sx={{
                height: 1,
                '& .simplebar-content': {
                    height: 1,
                    display: 'flex',
                    flexDirection: 'column'
                },
            }}
        >

            {(() => {
                if (isSuperAdminPath) return <SuperAdminMenu />;
                if (isSettingsPath) return <SettingsMenu selected={selectedPracticeForMenu} />;
                return <NavSectionVertical navConfig={whatsappConfig} isPractitioner={isPractitionerFlagUser} />;
            })()}
            {/* <NavSectionVertical navConfig={whatsappConfig} /> */}
            {!isPractitionerFlagUser && whatsappConfig.length ? <Box sx={{ flexGrow: 1, ...userIconWrapperStyle }} >
                <Box sx={userIconInnerStyle}>
                    <Tooltip title="Add User" arrow>
                        <IconButton onClick={() => setUserOpen(true)} sx={{ p: 0 }}>
                            <Iconify icon={'wpf:adduser'} width={18} height={18} />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box> : null}

            <NavbarDocs />
        </Scrollbar>
    );


    return (
        <Box
            component="nav"
            sx={{
                flexShrink: { lg: 0 },
                width: { lg: NAV.W_DASHBOARD },
            }}
        >
            {!isSuperAdminPath && !isSettingsPath && <CollapseButton />}
            {isDesktop ? (
                <Drawer
                    open
                    variant="permanent"
                    PaperProps={{
                        sx: {
                            zIndex: 0,
                            width: NAV.W_DASHBOARD,
                            bgcolor: 'common.white',
                            borderRightStyle: 'dashed',
                        },
                    }}
                >
                    {(results.length && whatsappConfig.length) ? renderContent :
                        <Box sx={widgetLoadingStyle}>
                            <NavbarSkeleton />
                        </Box>}
                </Drawer>
            ) : (
                <Drawer
                    open={openNav}
                    onClose={onCloseNav}
                    ModalProps={{
                        keepMounted: true,
                    }}
                    PaperProps={{
                        sx: {
                            width: NAV.W_DASHBOARD,
                        },
                    }}
                >
                    {(results.length && whatsappConfig.length) ? renderContent : <Box sx={widgetLoadingStyle}>
                        <NavbarSkeleton />
                    </Box>}
                </Drawer>
            )}
            <AddUserPopup userOpen={userOpen} setUserOpen={setUserOpen} />
        </Box>
    );
}

NavbarVertical.propTypes = {
    openNav: PropTypes.bool,
    onCloseNav: PropTypes.func,
};
